import ReactTableTrademoUntyped from "@shared/trademo-datatable/react-table-trademo";
import commaFormat from "@utils/commaFormat";
import { getTitleCase } from "@utils/utils";
import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import { Tooltip as ReactTooltip5 } from "react-tooltip5";
// @ts-expect-error -- the project has no type declarations for image assets; Vite resolves this to a URL.
import LoaderImg from "../../../assets/images/loader.gif";
import { CRITERIA_TOOLTIP, PAGE_SIZE, SIGNAL_TOOLTIP } from "../constants";
import { buildCriteriaGroups, matchesCriteria, pruneCriteriaSelection } from "../criteria";
import type { RecommendationRow, SortDirection } from "../types";
import CriteriaFilter from "./CriteriaFilter";
import type { CriteriaSelection } from "./CriteriaFilter";
import EmptyRecommendations from "./EmptyRecommendations";
import { RecommendationsTableStyles } from "./recommendationsTableStyles";

const ReactTableTrademo = ReactTableTrademoUntyped as unknown as (props: Record<string, unknown>) => ReactElement;

const TOOLTIP_ID = "ai-recommendations-table-tooltip";

type RecommendationsTableProps = {
  rows: RecommendationRow[];
  loading: boolean;
  clearSelection: boolean;
  onSelectionChange: (rows: RecommendationRow[]) => void;
  onCompanyClick: (row: RecommendationRow) => void;
  onBookmark: (row: RecommendationRow) => void;
  onDismiss: (row: RecommendationRow) => void;
  dismissingId: string | null;
  emptyText: string;
  showSignal: boolean;
};

// ReactTableTrademo tracks selection by a 1-based numeric `id` matching the row's position in `data`.
type TableRow = { id: number; row: RecommendationRow };
type CellProps = { row: { original: TableRow } };

// Signal chip backgrounds (Zeplin: Emerging #c7deff, Lookalike #c3fffc).
const SIGNAL_CHIP_CLASS: Record<string, string> = { emerging: "tw-bg-[#c7deff]", lookalike: "tw-bg-[#c3fffc]" };

const NEXT_SORT: Record<string, SortDirection> = { null: "desc", desc: "asc", asc: null };
const SORT_ICON: Record<string, string> = { null: "swap_vert", desc: "arrow_downward", asc: "arrow_upward" };

const Icon = ({ name, className = "" }: { name: string; className?: string }) => <span className={`material-symbols-outlined tw-flex tw-size-5 tw-items-center tw-justify-center tw-text-base tw-leading-none ${className}`}>{name}</span>;

const InfoIcon = ({ content }: { content: string }) => (
  <span data-tooltip-id={TOOLTIP_ID} data-tooltip-content={content} data-tooltip-place="bottom" className="tw-cursor-default">
    <Icon name="info" className="tw-text-gray-900" />
  </span>
);

const ShipmentCount = ({ label, value }: { label: string; value: number }) => (
  <span className="tw-flex tw-items-center tw-whitespace-nowrap tw-text-sm">
    <span className="tw-text-gray-500">
      {label} : <span className="tw-text-gray-900">{commaFormat(value)}</span>
    </span>
  </span>
);

const RecommendationsTable = ({ rows, loading, clearSelection, onSelectionChange, onCompanyClick, onBookmark, onDismiss, dismissingId, emptyText, showSignal }: RecommendationsTableProps) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const [sort, setSort] = useState<SortDirection>(null);
  const [criteriaSelection, setCriteriaSelection] = useState<CriteriaSelection>({});
  const criteriaGroups = useMemo(() => buildCriteriaGroups(rows), [rows]);

  // A refetch (dismiss, bookmark) keeps the user's filter, minus options that no longer exist.
  // Adjusted during render so the table never renders with the stale selection.
  const [prevRows, setPrevRows] = useState(rows);
  if (prevRows !== rows) {
    setPrevRows(rows);
    setCriteriaSelection((selection) => pruneCriteriaSelection(selection, criteriaGroups));
  }

  const tableData = useMemo<TableRow[]>(() => {
    const filtered = rows.filter((row) => matchesCriteria(row, criteriaSelection));
    const direction = sort === "desc" ? -1 : 1;
    const sorted = sort ? [...filtered].sort((a, b) => (a.totalShipments - b.totalShipments) * direction) : filtered;
    return sorted.map((row, index) => ({ id: index + 1, row }));
  }, [rows, criteriaSelection, sort]);

  // The table resets to its first page whenever `data` changes. Reset the page counter in the same
  // render (not in an effect) so a remount after a refetch starts on page 1 and the footer count matches.
  const [pagedData, setPagedData] = useState(tableData);
  if (pagedData !== tableData) {
    setPagedData(tableData);
    setPage(1);
  }

  const columns = useMemo(
    () =>
      [
        {
          key: "company",
          name: "Recommended companies",
          miWidth: 280,
          maWidth: 2000,
          cell: ({ row: { original } }: CellProps) => (
            <button type="button" onClick={() => onCompanyClick(original.row)} title={getTitleCase(original.row.companyName)} className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2.5 tw-bg-transparent tw-p-0 tw-text-left tw-text-sm tw-font-normal tw-text-primary-purple-600 hover:tw-underline">
              <span className="tw-min-w-0 tw-truncate tw-capitalize">{getTitleCase(original.row.companyName)}</span>
              <Icon name="arrow_forward" className="tw-flex-shrink-0 !tw-text-xs tw-text-gray-900" />
            </button>
          ),
        },
        {
          key: "criteria",
          name: (
            <span className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2">
              <span className="tw-flex tw-items-center">
                Recommendation criteria
                <InfoIcon content={CRITERIA_TOOLTIP} />
              </span>
              <CriteriaFilter groups={criteriaGroups} selection={criteriaSelection} onChange={setCriteriaSelection} />
            </span>
          ),
          miWidth: 265,
          maWidth: 265,
          cell: ({ row: { original } }: CellProps) => (original.row.criteria ? <span className="tw-line-clamp-2 tw-w-fit tw-max-w-full tw-break-words tw-rounded tw-bg-gray-100 tw-px-1.5 tw-py-0.5 tw-text-xs tw-text-gray-900">{original.row.criteria}</span> : null),
        },
        showSignal && {
          key: "signal",
          name: (
            <span className="tw-flex tw-items-center tw-justify-center tw-gap-2.5">
              Signal
              <InfoIcon content={`Emerging: ${SIGNAL_TOOLTIP.emerging}\nLookalike: ${SIGNAL_TOOLTIP.lookalike}`} />
            </span>
          ),
          miWidth: 160,
          maWidth: 160,
          cell: ({ row: { original } }: CellProps) =>
            original.row.signal ? (
              <span className="tw-flex tw-justify-center">
                <span className={`tw-rounded tw-px-1.5 tw-py-0.5 tw-text-xs tw-capitalize tw-text-gray-900 ${SIGNAL_CHIP_CLASS[original.row.signal] ?? "tw-bg-gray-100"}`}>{original.row.signal}</span>
              </span>
            ) : null,
        },
        {
          key: "shipments",
          name: (
            <button type="button" onClick={() => setSort((prev) => NEXT_SORT[String(prev)])} className="tw-flex tw-items-center tw-gap-1 tw-bg-transparent tw-p-0 tw-text-gray-900 tw-[font:inherit] tw-tracking-[inherit]">
              Shipments in last 6 months
              <Icon name={SORT_ICON[String(sort)]} className={sort ? "tw-text-primary-purple-600" : "tw-text-gray-900"} />
            </button>
          ),
          miWidth: 287,
          maWidth: 287,
          cell: ({ row: { original } }: CellProps) => {
            const { imports, exports, totalShipments } = original.row;
            return (
              <div className="tw-flex tw-items-center tw-gap-2.5">
                {imports !== undefined && <ShipmentCount label="Imports" value={imports} />}
                {imports !== undefined && exports !== undefined && <span className="tw-h-[22px] tw-w-0.5 tw-flex-shrink-0 tw-bg-gray-300" aria-hidden="true" />}
                {exports !== undefined && <ShipmentCount label="Exports" value={exports} />}
                {imports === undefined && exports === undefined && <span className="tw-text-sm tw-text-gray-900">{commaFormat(totalShipments)}</span>}
              </div>
            );
          },
        },
        {
          key: "actions",
          name: <span className="tw-block tw-text-center">Actions</span>,
          miWidth: 96,
          maWidth: 96,
          cell: ({ row: { original } }: CellProps) => (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
              <button type="button" onClick={() => onBookmark(original.row)} data-tooltip-id={TOOLTIP_ID} data-tooltip-content="Bookmark" aria-label={`Bookmark ${original.row.companyName}`} className="tw-flex tw-rounded tw-bg-transparent tw-p-0 tw-text-gray-900 hover:tw-text-primary-purple-600">
                <span className="material-symbols-outlined !tw-text-xl tw-leading-none">star</span>
              </button>
              <button type="button" onClick={() => onDismiss(original.row)} disabled={dismissingId === original.row.id} data-tooltip-id={TOOLTIP_ID} data-tooltip-content="Dismiss" aria-label={`Dismiss ${original.row.companyName}`} className="tw-flex tw-rounded tw-bg-transparent tw-p-0 tw-text-gray-900 hover:tw-text-primary-purple-600 disabled:tw-opacity-40">
                <span className="material-symbols-outlined !tw-text-xl tw-leading-none">delete</span>
              </button>
            </div>
          ),
        },
      ].filter(Boolean),
    [criteriaGroups, criteriaSelection, dismissingId, onBookmark, onCompanyClick, onDismiss, showSignal, sort]
  );

  const isFiltered = Object.values(criteriaSelection).some((values) => values.length > 0);

  if (loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-gray-200 tw-py-20">
        <img src={LoaderImg} alt="Loading" className="tw-w-12" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="tw-rounded-lg tw-border tw-border-gray-200">
        <EmptyRecommendations text={emptyText} />
      </div>
    );
  }

  return (
    <>
      <ReactTableTrademo
        columns={columns}
        data={tableData}
        paginationTotalRows={tableData.length}
        paginationPerPage={perPage}
        paginationDefaultPage={page}
        onChangePage={(nextPage: number, nextPerPage: number) => {
          setPage(nextPage);
          setPerPage(nextPerPage);
        }}
        onChangeRowsPerPage={setPerPage}
        onTableObjectChange={({ currentPage, perPage: nextPerPage }: { currentPage: number; perPage: number }) => {
          setPage(currentPage + 1);
          setPerPage(nextPerPage);
        }}
        onSelectedRowsChange={(selected: TableRow[]) => onSelectionChange((selected || []).map((item) => item.row))}
        selectedData={[]}
        clearSelectedRows={clearSelection}
        hideSorting={true}
        hideBorder={false}
        StyledComponent={RecommendationsTableStyles}
        noDataComponent={
          isFiltered ? (
            <span className="tw-flex tw-flex-col tw-items-center tw-gap-2">
              No recommended companies match the selected criteria.
              <button type="button" onClick={() => setCriteriaSelection({})} className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-primary-purple-500 tw-underline hover:tw-text-primary-purple-600">
                Clear filters
              </button>
            </span>
          ) : (
            emptyText
          )
        }
      />
      <ReactTooltip5 id={TOOLTIP_ID} className="trademo-tooltip tw-max-w-[320px] tw-whitespace-pre-line !tw-text-xs !tw-z-50" place="top" />
    </>
  );
};

export default RecommendationsTable;
